import { getOrgInfo } from "api/organisation";
import {
  createPaymentForEaseBuzz,
  createRazorpayOrder,
  createBilldeskOrder,
  checkBilldeskPaymentStatus,
  decryptKeysFromServer,
  encryptAndSendKeys,
  sendPaymentCancelledEvents,
  verifyRazorpaySignature,
} from "api/payment";
import { Navigate } from "react-router-dom";
import {
  setIsPaymentInProgess,
  setIsPaymentInitiationError,
} from "redux/formDisplay";
import { setIsOnlinePaymentInitiated } from "redux/paymentReducer";
import { v4 as uuidv4 } from "uuid";

/**
 * Display Payment Gateway and Initiate Payment
 *
 * This function loads the payment gateway script, and based on the selected gateway
 * (e.g., Razorpay or EaseBuzz), it either creates a new payment order and handles the payment
 * process using Razorpay, or opens the payment URL for EaseBuzz. It also verifies the payment
 * signature(RazorPay) and updates the payment status accordingly.
 *
 * @param {object} options - Options for displaying the payment gateway and processing the payment.
 * @param {object} options.paymentInfo - Information about the payment (eg: amount, currency).
 * @param {function} options.dispatch - Redux dispatch function for making API calls.
 * @param {function} options.handlePaymentUpdate - Callback to update the payment status.
 * @param {object} options.gatewayConfig - Configuration for the selected payment gateway.
 * @param {string} options.next_status_to_update - Next status to update after payment (optional).
 * @param {object} options.payment_content - Payment content to be displayed on success page (optional).
 * @throws Throws an error if any issue occurs during payment initiation or verification.
 */

export const displayPaymentGateway = async ({
  paymentDetails: paymentInfo,
  dispatch,
  gatewayConfig,
  selectedData,
  handlePaymentResult,
  decryptedId,
  paymentConfig,
  payment_settings
}) => {

  // Load the payment gateway script

if (gatewayConfig.gatewayName === "razorpay") {
    gatewayConfig?.scriptURL && (await loadScript(gatewayConfig?.scriptURL));
    let amount = null;
    if (selectedData?.payment_type?.toLowerCase() === "partial") {
      amount = Number(selectedData?.partial_amount);
    } else if (
      paymentInfo?.user_data?.max_amount ||
      paymentInfo?.pending_amount
    ) {
      let pending_amount = typeof paymentInfo?.pending_amount === "string" ? paymentInfo?.pending_amount.replace(/,/g, '') : paymentInfo?.pending_amount
      amount =
        paymentInfo?.user_data?.max_amount !== null &&
        paymentInfo?.user_data?.max_amount !== undefined
          ? Number(paymentInfo?.user_data?.max_amount)
          : Number(pending_amount);
    }
    const createOrderPayload = {
      amount,
      currency: paymentInfo?.currency || "INR",
      payment_capture: paymentInfo?.payment_capture || 1,
      key: gatewayConfig?.key,
      secret: gatewayConfig?.secret,
    };

    try {
      const orderResponse = await dispatch(
        createRazorpayOrder(createOrderPayload, gatewayConfig?.gatewayName)
      ).unwrap();
      dispatch(setIsPaymentInProgess(false));
      dispatch(setIsPaymentInitiationError(false));

      if (orderResponse.status === "success") {
        const { amount, id: order_id, currency } = orderResponse?.data || {};

        const userToken = localStorage.getItem("userToken");

        const options = {
          key: createOrderPayload.key,
          amount,
          currency,
          name: gatewayConfig?.userName,
          description: gatewayConfig?.description || "Test Transaction",
          order_id,
          handler: async function (response) {
            const data = {
              paymentId: response.razorpay_payment_id,
              orderId: response.razorpay_order_id,
              key: createOrderPayload.key,
              secret: createOrderPayload.secret,
              signature: response.razorpay_signature,
              ...createOrderPayload,
              payment_details: {
                ...paymentInfo,
                payment_status: "success",
                payment_gateway: gatewayConfig?.gatewayName,
              },
              payment_mode: selectedData?.payment_mode?.toLowerCase(),
              payment_type: paymentInfo?.disable_partial
                ? "full"
                : selectedData?.payment_type?.toLowerCase(),
              is_paid_by_internal_user: !!(
                userToken && Object.keys(JSON.parse(userToken))?.length
              ),
              paid_by_user_id:
                userToken &&
                Object.keys(JSON.parse(userToken))?.length &&
                JSON.parse(userToken)?.userId,
              paid_for_user_id: decryptedId?.userId,
              transfer_mode : gatewayConfig?.transfer_mode,
              transfers : gatewayConfig?.transfers,
              payment_settings:payment_settings
              
            };

            try {
              const signatureResponse = await dispatch(
                verifyRazorpaySignature(data, gatewayConfig.gatewayName)
              ).unwrap();

              const paymentStatus =
                signatureResponse.status === "success" ? "success" : "failure";

              if (paymentStatus === "success") {
                const stateData = {
                  header: payment_settings?.payment_completed_header||"Payment Successful",
                  content: payment_settings?.payment_completed_content||"Your payment has been successfully completed.",
                  isPaymentSuccess: true,
                };

                const urlParams = {
                  encryptedUserID: decryptedId?.userId,
                  encryptedPaymentTransactionId:
                    decryptedId?.paymentTransactionId,
                };
                dispatch(setIsOnlinePaymentInitiated(false));

                handlePaymentResult(
                  paymentStatus,
                  stateData,
                  urlParams,
                  "/payment-success"
                );
                // Navigate("/success", { state: stateData });
              }
              if (paymentStatus === "failure") {
                const stateData = {
                  header: "Payment Failed",
                  content: "Your payment has been failed.",
                  isPaymentSuccess: false,
                };
                const urlParams = {
                  encryptedUserID: decryptedId?.userId,
                  encryptedPaymentTransactionId:
                    decryptedId?.paymentTransactionId,
                };
                dispatch(setIsOnlinePaymentInitiated(false));

                handlePaymentResult(
                  paymentStatus,
                  stateData,
                  urlParams,
                  "/payment-success"
                );
              }

              dispatch(setIsPaymentInProgess(false));
              dispatch(setIsPaymentInitiationError(false));

              // handlePaymentUpdate(
              //   next_status_to_update || "Payment completed",
              //   payment_content,
              //   paymentStatus
              // );
            } catch (error) {
              console.error("Error verifying signature:", error);
              dispatch(setIsOnlinePaymentInitiated(false));

              // handlePaymentUpdate(
              //   next_status_to_update || "Payment completed",
              //   payment_content,
              //   "failure"
              // );
            }
          },
          modal: {
            ondismiss: (err) => {
              const stateData = {
                header: "Payment Failed",
                content: "Your payment has been failed.",
                isPaymentSuccess: false,
              };
              const urlParams = {
                encryptedUserID: decryptedId?.userId,
                encryptedPaymentTransactionId:
                  decryptedId?.paymentTransactionId,
              };
              dispatch(setIsOnlinePaymentInitiated(false));

              const payload = {
                event: "UserCancelled",
                cancelled_at: new Date().toISOString(),
                cancelled_by: userToken
                  ? Object.keys(JSON.parse(userToken))?.length &&
                    JSON.parse(userToken)?.userId
                  : decryptedId?.userId,
                amount: amount,
                payment_mode: selectedData?.payment_mode?.toLowerCase(),
                payment_type: paymentInfo?.disable_partial
                  ? "full"
                  : selectedData?.payment_type?.toLowerCase(),
                err: err,
                user_data: paymentInfo?.user_data,
                payment_id: decryptedId?.paymentTransactionId,
              };

              dispatch(sendPaymentCancelledEvents(payload));

              handlePaymentResult(
                false,
                stateData,
                urlParams,
                "/payment-success"
              );
            },
            // onhidden: (err) => {
            //   const stateData = {
            //     header: "Payment Failed",
            //     content: "Your payment has been failed.",
            //     isPaymentSuccess: true,
            //   };
            //   const urlParams = {
            //     encryptedUserID: decryptedId?.userId,
            //     encryptedPaymentTransactionId:
            //       decryptedId?.paymentTransactionId,
            //   };
            //   dispatch(setIsOnlinePaymentInitiated(false));

            //   handlePaymentResult(
            //     false,
            //     stateData,
            //     urlParams,
            //     "/payment-success"
            //   );
            // },
          },
          prefill: gatewayConfig.prefill || {},
          notes: gatewayConfig.notes || {},
          theme: gatewayConfig.theme || { color: "#61dafb" },
        };

        const paymentObject = new window[
          gatewayConfig.paymentObject || "razorpay"
        ](options);
        paymentObject.open();
      } else {
        const stateData = {
          header: "Payment Failed",
          content: "Something went wrong. Please try again later.",
          isPaymentSuccess: false,
        };
        const urlParams = {
          encryptedUserID: decryptedId?.userId,
          encryptedPaymentTransactionId: decryptedId?.paymentTransactionId,
        };

        handlePaymentResult(false, stateData, urlParams, "/payment-success");
        console.error("Error creating order:", orderResponse.error);
        dispatch(setIsPaymentInProgess(false));
        dispatch(setIsPaymentInitiationError(true));
        dispatch(setIsOnlinePaymentInitiated(false));
      }
    } catch (error) {
      const stateData = {
        header: "Payment Failed",
        content: "Something went wrong. Please try again later.",
        isPaymentSuccess: false,
      };
      const urlParams = {
        encryptedUserID: decryptedId?.userId,
        encryptedPaymentTransactionId: decryptedId?.paymentTransactionId,
      };

      handlePaymentResult(false, stateData, urlParams, "/payment-success");
      console.error("Error creating Razorpay order:", error);
      dispatch(setIsPaymentInProgess(false));
      dispatch(setIsPaymentInitiationError(true));
      dispatch(setIsOnlinePaymentInitiated(false));
    }
  } else if(gatewayConfig.gatewayName === "billdesk"){
    
     let amount = null;
     if (
       paymentInfo?.user_data?.max_amount ||
       paymentInfo?.pending_amount
     ) {
       let pending_amount = typeof paymentInfo?.pending_amount === "string" ? paymentInfo?.pending_amount.replace(/,/g, '') : paymentInfo?.pending_amount
       amount =
         paymentInfo?.user_data?.max_amount !== null &&
         paymentInfo?.user_data?.max_amount !== undefined
           ? Number(paymentInfo?.user_data?.max_amount)
           : Number(pending_amount);
           const userToken = localStorage.getItem("userToken");
           const createOrderPayload = {
            amount,
            payment_id:paymentInfo?._id,
            currency: gatewayConfig?.currency || "356",
            key: gatewayConfig?.key,
            secret: gatewayConfig?.secret,
            mercid:gatewayConfig?.merchant_id,
            itemcode :gatewayConfig?.itemcode,
            orderid :uuidv4(),
            additional_info :gatewayConfig?.additional_info || {},
            org_identifier:gatewayConfig?.org_identifier || "",
            payment_additional_data :{
              payment_mode: selectedData?.payment_mode?.toLowerCase(),
              user_data : paymentInfo.user_data,
              amount:amount,
              payment_type: paymentInfo?.disable_partial
                ? "full"
                : selectedData?.payment_type?.toLowerCase(),
              is_paid_by_internal_user: !!(
                userToken && Object.keys(JSON.parse(userToken))?.length
              ),
              paid_by_user_id:
                userToken &&
                Object.keys(JSON.parse(userToken))?.length &&
                JSON.parse(userToken)?.userId,
              paid_for_user_id: decryptedId?.userId,
            }

          };
          try {
            const orderResponse = await dispatch(
              createBilldeskOrder(createOrderPayload, gatewayConfig?.gatewayName)
            ).unwrap();
            if (orderResponse.status === "success") {
              let orderId = orderResponse?.data?.response?.orderid;
              localStorage.setItem("orderId", orderId);
              let formContent = orderResponse?.data?.redirectionParams;
              let form = document.createElement("form");
              form.setAttribute("method", "POST");
              form.setAttribute("action", formContent?.url);
              form.setAttribute("name", "sdklaunch"); 
              form.setAttribute("id", "sdklaunch");
              form.setAttribute("target", "_blank");
              let bdOrderIdInput = document.createElement("input");
              bdOrderIdInput.setAttribute("type", "hidden");
              bdOrderIdInput.setAttribute("name", "bdOrderId");
              bdOrderIdInput.setAttribute("value", formContent?.parameters?.bdorderid);
              form.appendChild(bdOrderIdInput);
          
              let merchantIdInput = document.createElement("input");
              merchantIdInput.setAttribute("type", "hidden");
              merchantIdInput.setAttribute("name", "merchantId");
              merchantIdInput.setAttribute("value", formContent?.parameters?.mercid);
              form.appendChild(merchantIdInput);
          
              let rdataInput = document.createElement("input");
              rdataInput.setAttribute("type", "hidden");
              rdataInput.setAttribute("name", "rdata");
              rdataInput.setAttribute("value", formContent?.parameters?.rdata);
              form.appendChild(rdataInput);
          
              document.body.appendChild(form);
              document.forms["sdklaunch"].submit();
              checkPaymentStatusPeriodically(dispatch, orderId, decryptedId,handlePaymentResult,gatewayConfig?.org_identifier);

            }else{
              const stateData = {
                header: "Payment Failed",
                content: orderResponse?.message || "Something went wrong. Please try again later.",
                isPaymentSuccess: false,
              };
              const urlParams = {
                encryptedUserID: decryptedId?.userId,
                encryptedPaymentTransactionId: decryptedId?.paymentTransactionId,
              };
      
              handlePaymentResult(false, stateData, urlParams, "/payment-success");
              // console.error("Error creating order:", orderResponse.error);
              dispatch(setIsPaymentInProgess(false));
              dispatch(setIsPaymentInitiationError(true));
              dispatch(setIsOnlinePaymentInitiated(false));
            }
          } catch (error) {
            
          }
      
     }
    
         
  }
  else if (gatewayConfig.gatewayName === "easebuzz") {
    try {
      let payload = createEaseBuzzPayload(selectedData, gatewayConfig);
      payload = {
        ...payload,
        key: gatewayConfig.key,
        secret: gatewayConfig.secret,
        sub_merchant_id: gatewayConfig.sub_merchant_id,
        enable_iframe: gatewayConfig.enable_iframe,
        env: gatewayConfig.env,
        furl: gatewayConfig.furl,
        surl: gatewayConfig.surl,
        productinfo: gatewayConfig.productinfo,
      };

      const paymentURL = await dispatch(
        createPaymentForEaseBuzz(payload)
      ).unwrap();
      dispatch(setIsPaymentInProgess(false));
      dispatch(setIsPaymentInitiationError(false));

      if (paymentURL) {
        window.open(paymentURL, "_self");
      }
    } catch (error) {
      const stateData = {
        header: "Payment Failed",
        content: "Something went wrong. Please try again later.",
        isPaymentSuccess: false,
      };
      const urlParams = {
        encryptedUserID: decryptedId?.userId,
        encryptedPaymentTransactionId: decryptedId?.paymentTransactionId,
      };

      handlePaymentResult(false, stateData, urlParams, "/payment-success");

      console.error("Error creating EaseBuzz payment:", error);
      dispatch(setIsPaymentInProgess(false));
      dispatch(setIsPaymentInitiationError(true));
      dispatch(setIsOnlinePaymentInitiated(false));
    }
  } else {
    alert("Invalid payment gateway selected");
    dispatch(setIsOnlinePaymentInitiated(false));
  }
};

const checkPaymentStatusPeriodically = (dispatch, orderid, decryptedId,handlePaymentResult,org_identifier) => {
  const startTime = Date.now();
  const fiveMinutes = 5 * 60 * 1000; // 5 minutes

  const checkStatus = async () => {

    if (Date.now() - startTime > fiveMinutes) {
      return;
    }

    try {
      
      const statusResponse = await dispatch(
        checkBilldeskPaymentStatus({ orderid,org_identifier })
      ).unwrap();


      if (statusResponse?.data?.response?.auth_status == "0300") {
        const stateData = {
          header: "Payment Successfull",
          content:"The transaction was successful. Please check your inbox for updates"||"Something went wrong. Please try again later.",
          isPaymentSuccess: true,
        };
        const urlParams = {
          encryptedUserID: decryptedId?.userId,
          encryptedPaymentTransactionId: decryptedId?.paymentTransactionId,
        };

        handlePaymentResult(false, stateData, urlParams, "/payment-success");
        return;
      } else if (statusResponse?.data?.response?.auth_status == "0399") {
        const stateData = {
          header: "Payment Failed",
          content:"The transaction failed. Please retry payment using the link sent to your inbox" + 
                `. Reason: ${statusResponse?.data?.response?.transaction_error_desc}`||"Something went wrong. Please try again later.",
          isPaymentSuccess: false,
        };
        const urlParams = {
          encryptedUserID: decryptedId?.userId,
          encryptedPaymentTransactionId: decryptedId?.paymentTransactionId,
        };

        handlePaymentResult(false, stateData, urlParams, "/payment-success");
        // handlePaymentFailure(dispatch, decryptedId, handlePaymentResult, "Payment failed.");
        return;
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }

    // Schedule the next check AFTER 5 seconds
    setTimeout(checkStatus, 10000);
  };

  // Start the first check
  checkStatus();
};


/**
 * Handles the payment process based on selected payment method and organization information.
 *
 * This function orchestrates the payment process by fetching organization information, decrypting
 * relevant keys, configuring the payment gateway, and initiating the payment flow. It also handles
 * errors during the process
 *
 * @param {Object} options - The options for handling the payment.
 * @param {function} options.dispatch - The Redux dispatch function.
 * @param {function} options.handlePaymentUpdate - The function to handle payment updates.
 * @param {string} [options.selectedPaymentMethod="razorpay"] - The selected payment method.
 * @param {Object} [options.paymentInfo={}] - Information about the payment.
 * @param {boolean} [options.next_status_to_update] - Contains the status to be updated.
 * @param {string} [options.payment_content] - The content related to the payment successful page
 * @returns {void}
 */
export async function handlePayment({
  dispatch,
  paymentDetails,
  selectedData,
  selectedPaymentMethod,
  paymentConfig,
  additional_info,
  payment_settings,
  org_identifier,
  handlePaymentResult,
  decryptedId,
  organisation_id,
  verifyPaymentStatusAndProceed
}) {
  try {
    dispatch(setIsPaymentInProgess(true));
    dispatch(setIsOnlinePaymentInitiated(true));
    dispatch(setIsPaymentInitiationError(false));
    const organisationInfo = await getOrgInfo({
      _id: organisation_id, 
    });
    const hasNoPendingAmount = await verifyPaymentStatusAndProceed();

    if (hasNoPendingAmount) return;

    if (!organisationInfo?.payment_gateway_settings?.[selectedPaymentMethod]) {
      return;
    }



      const gatewayConfig = getGatewayConfig(
        selectedPaymentMethod,
        organisationInfo.payment_gateway_settings[selectedPaymentMethod],
        additional_info,
        org_identifier
      );

      if (gatewayConfig) {
        displayPaymentGateway({
          dispatch,
          paymentDetails,
          selectedData,
          selectedPaymentMethod,
          gatewayConfig,
          handlePaymentResult,
          decryptedId,
          paymentConfig,
          payment_settings
        });
      } else {
        alert("Invalid payment method selected");
        dispatch(setIsOnlinePaymentInitiated(false));
      }
  
  } catch (error) {
    console.error("Error fetching organization info:", error);
    dispatch(setIsOnlinePaymentInitiated(false));
  }
}

/**
 * Retrieves the configuration object for the selected payment method.
 *
 * This function returns a configuration object based on the selected payment method and provided payment gateway information.
 * If the selected payment method is not recognized, it returns null. The configuration object includes properties like gatewayName,
 * scriptURL, key, secret, userName, paymentObject, prefill, notes, and theme, with default values provided as fallbacks.
 *
 * @param {string} selectedPaymentMethod - The selected payment method (e.g., "razorpay", "easebuzz").
 * @param {object} paymentGateWayInfo - Information related to the payment gateway.
 * @returns {object|null} - The configuration object for the selected payment method, or null if the method is not recognized.
 */

function getGatewayConfig(selectedPaymentMethod, paymentGateWayInfo, additional_info,org_identifier) {
  switch (selectedPaymentMethod) {
    case "billdesk":
      return {
        gatewayName: "billdesk",
        key: paymentGateWayInfo?.key,
        secret: paymentGateWayInfo?.secret,
        merchant_id: paymentGateWayInfo?.merchant_id,
        userName: paymentGateWayInfo?.userName || "",
        paymentObject: paymentGateWayInfo?.paymentObject || "billdesk",
        additional_info:additional_info,
        org_identifier:org_identifier,
        prefill: paymentGateWayInfo?.prefill || {
          name: "",
          email: "",
          contact: "9999999999",
        },
        notes: paymentGateWayInfo?.notes,
        itemcode: paymentGateWayInfo?.itemcode,
        currency : paymentGateWayInfo?.currency || 356
      };
    case "razorpay":
      return {
        gatewayName: "razorpay",
        scriptURL:
          paymentGateWayInfo?.script ||
          "https://checkout.razorpay.com/v1/checkout.js",
        key: paymentGateWayInfo.key || "rzp_test_11lXniMX5EKwXS",
        secret: paymentGateWayInfo.secret,
        userName: paymentGateWayInfo.userName || "",
        paymentObject: paymentGateWayInfo.paymentObject || "Razorpay",
        prefill: paymentGateWayInfo.prefill || {
          name: "",
          email: "",
          contact: "9999999999",
        },
        notes: paymentGateWayInfo.notes || {
          address: "Soumya Dey Corporate Office",
        },
        theme: paymentGateWayInfo.theme || {
          color: "#61dafb",
        },
        transfer_mode:paymentGateWayInfo?.transfer_mode || null,
        transfers:paymentGateWayInfo?.transfers || null
      };
    case "easebuzz":
      return {
        gatewayName: "easebuzz",
        key: paymentGateWayInfo?.key,
        secret: paymentGateWayInfo?.secret,
        sub_merchant_id: paymentGateWayInfo?.sub_merchant_id,
        enable_iframe: paymentGateWayInfo?.enable_iframe,
        env: paymentGateWayInfo?.env,
        ...paymentGateWayInfo,
      };
    default:
      return null;
  }
}

function loadScript(src = "") {
  return new Promise((resolve) => {
    const existingScript = document.querySelector(`script[src="${src}"]`);

    if (existingScript) {
      resolve(true);
    } else {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    }
  });
}

const createEaseBuzzPayload = (selectedData, gatewayConfig) => {
  let payload = {};
  Object.keys(gatewayConfig.auto_fill).forEach((key) => {
    if (eval(`${gatewayConfig.auto_fill[key]}`)) {
      const value = eval(`${gatewayConfig.auto_fill[key]}`);
      payload[key] = value;
    }
  });
  payload["txnid"] = uuidv4();
  payload["amount"] = Number(selectedData["amount"]) || 10;
  payload["currency"] = selectedData["currency"] || "INR";

  return payload;
};
